import {registerLocaleData} from '@angular/common';
import nl from '@angular/common/locales/nl';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from "../../core/api/api.service";
import {AccessControlService} from "../../core/access-control/access-control.service";
import {customerRole} from "../../core/access-roles/customer.role";
import {
  FacilityTypeName,
  FirePartitionTypeName,
  Material,
  MATERIAL_STATUSES,
  MaterialTypesModel,
} from "../../core/models/firepartition.type";
import {formatDate, isoDatestring} from "../../core/utils/utils";

@Component({
    selector: 'bergh-edit-materials',
    templateUrl: './edit-materials.component.html',
    styleUrls: ['./edit-materials.component.scss'],
    standalone: false
})
export class EditMaterialsComponent implements OnInit, OnChanges {
  @Output() updateSaveMaterialsStatus: EventEmitter<Material[] | undefined> = new EventEmitter();
  @Output() flaggedDirty = new EventEmitter<void>();
  @Input() saveMaterialsStatus: boolean;
  @Input() firePartitionId;
  @Input() facilityType: FacilityTypeName | undefined;
  @Input() firePartitionType: FirePartitionTypeName;
  materialTypes: MaterialTypesModel[] = [];
  editMaterials: any[];

  @Input()
  set materials(materials: Material[]) {
    this.editMaterials = materials.map(material => ({
      materialId: material._id,
      materialTypeId: material.materialType._id,
      materialSpecificationId: material.materialSpecification?._id,
      materialSpecificationName: material.materialSpecification?.name,
      materialSpecificationText: material.materialSpecificationText,
      materialSpecificationBrand: material.materialSpecificationBrand,
      materialRemarks: material.materialRemarks,
      materialStatus: material.materialStatus,
      materialPlacedDate: formatDate(material.materialPlacedDate),
      materialMaintenanceDate: formatDate(material.materialMaintenanceDate),
      materialMaintenanceIntervalMonths: material.materialMaintenanceIntervalMonths,
      noOfUnits: material.noOfUnits,
    }));
  }

  deletedMaterialIds: string[] = [];
  allowEdit: boolean;
  isEditMaterialModalOpen: boolean = false;
  editedMaterial: Material | null = null;
  editedPosition = -1;

  constructor(
    private _api: ApiService,
    private _toastr: ToastrService,
    router: Router,
    accessControl: AccessControlService,
  ) {
    this.allowEdit = accessControl.getRoleCode() != customerRole.code && !router.url.includes("afdichtingen")
    registerLocaleData(nl)
  }

  ngOnInit(): void {

    void this.getMaterialTypes();
  }

  ngOnChanges() {
    if (this.saveMaterialsStatus) {
      this.saveMaterials()
    }
  }

  async getMaterialTypes() {
    const allMaterialTypes = await this._api.getMaterialTypes()
    if (this.firePartitionType === FirePartitionTypeName.INSTALLATION_TECHNICAL_FACILITY) {
      this.materialTypes = allMaterialTypes.filter(materialType => materialType.forFacilityTypes && materialType.forFacilityTypes.includes(this.facilityType))
    } else {
      this.materialTypes = allMaterialTypes.filter(materialType => !materialType.forFacilityTypes)
    }
  }

  getMaterialType(materialTypeId: string) {
    return this.materialTypes.find((type) => type._id === materialTypeId)
  }

  addNewMaterial() {
    this.editedMaterial = null
    this.editedPosition = -1
    this.isEditMaterialModalOpen = true;
  }

  editMaterial(i: number) {
    this.editedMaterial = this.editMaterials[i]
    this.editedPosition = i
    this.isEditMaterialModalOpen = true;
  }

  onSaveMaterial(materialData: any) {
    if (this.editedPosition === -1) {
      this.editMaterials.push({materialId: null, ...materialData});
    } else {
      Object.assign(this.editMaterials[this.editedPosition], materialData)
      this.editedPosition = -1
    }
    this.isEditMaterialModalOpen = false;
    this.flagDirty()
  }

  onCancelModal() {
    this.isEditMaterialModalOpen = false;
  }

  removeMaterial(i: number) {
    const materialId = this.editMaterials[i].materialId;
    if (materialId) {
      this.deletedMaterialIds.push(materialId);
    }
    this.editMaterials.splice(i, 1)
    this.flagDirty()
  }

  saveMaterials() {
    this._api.updateMaterials(this.firePartitionId, this.getUpdateMaterialsPayload()).then((result) => {
        this._toastr.success("Materialen opgeslagen")
        this.materials = result
        this.deletedMaterialIds = []
        this.updateSaveMaterialsStatus.emit(result)
      }, () => {
        this.updateSaveMaterialsStatus.emit(undefined)
      }
    )
  }

  private getUpdateMaterialsPayload() {
    return {
      materials: this.editMaterials.map(material => ({...material,
        materialPlacedDate: isoDatestring(material.materialPlacedDate),
        materialMaintenanceDate: isoDatestring(material.materialMaintenanceDate),
      })),
      deletedMaterialIds: this.deletedMaterialIds,
    }
  }

  flagDirty() {
    this.flaggedDirty.emit()
  }

  protected readonly FirePartitionTypeName = FirePartitionTypeName;
  protected readonly MATERIAL_STATUSES = MATERIAL_STATUSES;
  protected readonly formatDate = formatDate;
}
